/**
 * LinkedAccountsSection - Shows email/password and Google providers with link/unlink actions
 */

import { User as FirebaseUser } from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import { Mail, Check } from 'lucide-react';
import { User } from '@/types';

interface LinkedAccountsSectionProps {
  user: User;
  firebaseUser: FirebaseUser | null;
  hasGoogleLinked: boolean | undefined;
  hasPasswordLinked: boolean | undefined;
  linkingGoogle: boolean;
  onLinkGoogle: () => void;
  onUnlinkGoogle: () => void;
}

export default function LinkedAccountsSection({
  user,
  firebaseUser,
  hasGoogleLinked,
  hasPasswordLinked,
  linkingGoogle,
  onLinkGoogle,
  onUnlinkGoogle,
}: LinkedAccountsSectionProps) {
  const { t } = useTranslation();

  return (
    <div className="bg-surface border border-hairline rounded-xl p-6 mb-6">
      <h2 className="font-sans text-lg font-semibold text-ink mb-4">{t('profilePage.linkedAccounts')}</h2>

      <div className="space-y-3">
        {/* Email/Password Provider */}
        <div className="flex items-center justify-between p-4 border border-hairline rounded-lg bg-surface-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-surface-3 text-ink-subtle rounded-full flex items-center justify-center">
              <Mail className="w-5 h-5" aria-hidden="true" />
            </div>
            <div>
              <p className="font-medium text-ink">{t('profilePage.emailPassword')}</p>
              <p className="text-sm text-ink-muted">{user.email}</p>
            </div>
          </div>
          {hasPasswordLinked ? (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-success/10 text-success border border-success/30 text-xs font-medium rounded-full">
              <Check className="w-3 h-3" aria-hidden="true" /> {t('profilePage.linked')}
            </span>
          ) : (
            <span className="px-3 py-1 bg-surface-3 text-ink-muted text-xs font-medium rounded-full">
              {t('profilePage.notLinked')}
            </span>
          )}
        </div>

        {/* Google Provider */}
        <div className="flex items-center justify-between p-4 border border-hairline rounded-lg bg-surface-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-surface-3 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
            </div>
            <div>
              <p className="font-medium text-ink">{t('profilePage.googleSignIn')}</p>
              <p className="text-sm text-ink-muted">
                {hasGoogleLinked
                  ? firebaseUser?.providerData.find(p => p.providerId === 'google.com')?.email
                  : t('profilePage.notLinked')}
              </p>
            </div>
          </div>
          {hasGoogleLinked ? (
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-success/10 text-success border border-success/30 text-xs font-medium rounded-full">
                <Check className="w-3 h-3" aria-hidden="true" /> {t('profilePage.linked')}
              </span>
              <button
                onClick={onUnlinkGoogle}
                className="px-3 py-1 text-xs text-ink-subtle hover:text-danger rounded-lg transition-colors"
              >
                {t('profilePage.unlink')}
              </button>
            </div>
          ) : (
            <button
              onClick={onLinkGoogle}
              disabled={linkingGoogle}
              className="px-4 py-2 bg-accent text-on-accent text-sm font-medium rounded-lg hover:bg-accent-hover transition-colors disabled:opacity-50"
            >
              {linkingGoogle ? t('profilePage.linking') : t('profilePage.linkGoogle')}
            </button>
          )}
        </div>
      </div>

      <div className="mt-4 p-4 bg-surface-2 border border-hairline rounded-lg">
        <p className="text-sm text-ink-muted">
          <strong className="text-ink font-medium">{t('common.tip')}:</strong> {t('profilePage.linkTip')}
        </p>
      </div>
    </div>
  );
}
